/**
 * Users Service
 * API calls for user management operations
 */

import apiClient from './api.client';
import type { ApiResponse, User } from '@/types/api.types';

export interface UserListParams {
  search?: string;
  status?: 'all' | 'pending' | 'approved' | 'inactive';
  limit?: number;
  offset?: number;
}

export interface UserListResponse {
  users: User[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

/**
 * Get all users (authenticated users only)
 */
export const listUsers = async (params?: UserListParams): Promise<UserListResponse> => {
  const response = await apiClient.get<ApiResponse<UserListResponse>>('/users', {
    params,
  });

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch users');
  }

  return response.data.data;
};

/**
 * Get user by ID
 */
export const getUserById = async (userId: string): Promise<User> => {
  const response = await apiClient.get<ApiResponse<{ user: User }>>(
    `/users/${userId}`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch user');
  }

  return response.data.data.user;
};

/**
 * Admin: Get all users with filtering
 */
export const listUsersAdmin = async (params?: UserListParams): Promise<UserListResponse> => {
  const response = await apiClient.get<ApiResponse<UserListResponse>>('/admin/users', {
    params,
  });

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch users');
  }

  return response.data.data;
};

/**
 * Admin: Approve a pending user
 */
export const approveUser = async (userId: string): Promise<User> => {
  const response = await apiClient.post<ApiResponse<{ user: User }>>(
    `/admin/users/${userId}/approve`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to approve user');
  }

  return response.data.data.user;
};

/**
 * Admin: Change user role
 */
export const changeUserRole = async (
  userId: string,
  role: 'admin' | 'employee'
): Promise<User> => {
  const response = await apiClient.patch<ApiResponse<{ user: User }>>(
    `/admin/users/${userId}/role`,
    { role }
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to change user role');
  }

  return response.data.data.user;
};

/**
 * Admin: Deactivate user account
 */
export const deactivateUser = async (userId: string): Promise<User> => {
  const response = await apiClient.post<ApiResponse<{ user: User }>>(
    `/admin/users/${userId}/deactivate`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to deactivate user');
  }

  return response.data.data.user;
};

/**
 * Admin: Reactivate user account
 */
export const reactivateUser = async (userId: string): Promise<User> => {
  const response = await apiClient.post<ApiResponse<{ user: User }>>(
    `/admin/users/${userId}/reactivate`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to reactivate user');
  }

  return response.data.data.user;
};

/**
 * Admin: Permanently delete user (must be deactivated first)
 */
export const deleteUser = async (userId: string): Promise<{ id: string; deleted: boolean }> => {
  const response = await apiClient.delete<ApiResponse<{ id: string; deleted: boolean }>>(
    `/admin/users/${userId}`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to delete user');
  }

  return response.data.data;
};
